import type { NodeTypes } from '@mv/core/model/heim/richText';
import { cn } from '@mv/ui-kit/headless';
import { ListItemBullet } from '@mv/ui-kit/primitives';
import { Html } from '@mv/ui-kit/primitives';
import { Fragment } from 'react';
import type { RichTextContext } from '../RichText';
import { collectLinks } from './collectLinks';
import { getPlainText } from './getPlainText';
import { linkActionProps } from './inlineA11y';

type ListItemGroup =
	| { key: string; type: 'inline'; nodes: NodeTypes[] }
	| { key: string; type: 'list'; node: NodeTypes };

export function ListItem({ node, nodeKey, context }: { node: NodeTypes; nodeKey: number; context: RichTextContext }) {
	const value = (node as NodeTypes & { value?: unknown }).value;
	const listItemNumber = typeof value === 'number' ? value : (context.listStart ?? 1) + nodeKey;
	const groups = (node.children ?? []).reduce<ListItemGroup[]>((groups, child) => {
		if (child.type === 'list') {
			groups.push({ key: `group-${groups.length}`, type: 'list', node: child });
		} else {
			const lastGroup = groups.at(-1);
			if (lastGroup?.type === 'inline') {
				lastGroup.nodes.push(child);
			} else {
				groups.push({ key: `group-${groups.length}`, type: 'inline', nodes: [child] });
			}
		}
		return groups;
	}, []);

	// A nested list renders its own items, each already an accessibility element, so this item
	// speaks only its own inline text — otherwise the nested text is announced twice.
	const ownNodes = groups.flatMap((group) => (group.type === 'inline' ? group.nodes : []));
	const spokenText = getPlainText(ownNodes, context.replacements);
	const spokenLabel = context.listType === 'ordered' ? `${listItemNumber}. ${spokenText}` : spokenText;

	return (
		<Html.Li
			key={nodeKey}
			accessible={context.screenReaderEnabled}
			focusable={false}
			accessibilityLabel={context.screenReaderEnabled ? spokenLabel : undefined}
			{...(context.screenReaderEnabled ? linkActionProps(collectLinks(ownNodes, context)) : {})}
			className="my-0 min-w-0"
		>
			<Html.Div className="flex min-w-0 flex-row items-start">
				{context.listType === 'ordered' ? (
					<Html.Span className="mr-3 ml-0.5 shrink-0 font-body leading-6 text-primary">{listItemNumber}.</Html.Span>
				) : (
					<ListItemBullet className="mt-0.5 mr-3 ml-0.5 shrink-0 web:mt-1.25" />
				)}
				<Html.Div className="flex min-w-0 flex-1 flex-col gap-2">
					{groups.map((group) => {
						if (group.type !== 'inline') {
							return <Fragment key={group.key}>{context.renderChildren([group.node])}</Fragment>;
						}

						return (
							<Html.P
								key={group.key}
								className={cn(
									'my-0 font-body leading-6 text-primary',
									context.size === 'small' ? 'text-sm' : 'text-base',
								)}
							>
								{context.renderChildren(group.nodes)}
							</Html.P>
						);
					})}
				</Html.Div>
			</Html.Div>
		</Html.Li>
	);
}
